// Données pour la Communication Officielle PRIsME aux Primes
// Première communication interne - Format présentation structurée

export interface ParticipantPRIsME {
  id: string;
  nom: string;
  prenom: string;
  role: string;
  email: string;
  avatar: string;
}

export interface PointPresentation {
  id: string;
  titre: string;
  contenu: string;
  dureeMinutes: number;
  graphiques?: string[];
  concepts?: string[];
}

export interface OrdreDuJourPRIsME {
  id: string;
  numero: number;
  titre: string;
  description: string;
  presentateur: ParticipantPRIsME;
  points: PointPresentation[];
  dureeMinutes: number;
  niveau: 'Stratégique' | 'Tactique' | 'Opérationnel';
  statut: 'Planifié' | 'En cours' | 'Complété';
}

export interface CommunicationPRIsME {
  id: string;
  titre: string;
  date: string; // Format: YYYY-MM-DD
  heure: string; // Format: HH:MM
  dureeMinutes: number;
  lieu: string;
  objectifPrincipal: string;
  statut: 'Planifiée' | 'En cours' | 'Complétée';
  participants: ParticipantPRIsME[];
  ordreDuJour: OrdreDuJourPRIsME[];
}

export interface CarteComitePRIsME {
  id: string;
  titre: string;
  category: string;
  description: string;
  aiSuggestion: string;
  kpiScore: number;
  imageUrl: string;
  inKnowledgeBase: boolean;
  communication: CommunicationPRIsME;
}

// ============================================
// PARTICIPANTS COMITÉ PRIsME
// ============================================

export const participantsComitePRIsME: ParticipantPRIsME[] = [
  {
    id: 'jp',
    nom: 'Leclerc',
    prenom: 'Jean-Pierre',
    role: 'Directeur Service TI',
    email: 'jean-pierre.leclerc@bzinc.ca',
    avatar: '/JP.png'
  },
  {
    id: 'eh',
    nom: 'Hains',
    prenom: 'Eric',
    role: 'DA Service TI & Chargé de projet',
    email: 'eric.hains@bzinc.ca',
    avatar: '/EH.png'
  },
  {
    id: 'fm',
    nom: 'Marquis',
    prenom: 'Francis',
    role: 'Directeur Cybersécurité & Solutions',
    email: 'francis.marquis@bzinc.ca',
    avatar: '/FM.png'
  },
  {
    id: 'jmc',
    nom: 'Caouette',
    prenom: 'Jean-Michel',
    role: 'DA Service TI',
    email: 'jean-michel.caouette@bzinc.ca',
    avatar: '/JMC.png'
  },
  {
    id: 'plf',
    nom: 'Fortin',
    prenom: 'Pierre-Luc',
    role: 'Directeur de compte',
    email: 'pierre-luc.fortin@bzinc.ca',
    avatar: '/PLF.png'
  },
  {
    id: 'db',
    nom: 'Bourassa',
    prenom: 'Dany',
    role: 'Fondateur PRIsME & Président',
    email: 'dany.bourassa@bzinc.ca',
    avatar: '/DB.png'
  }
];

// ============================================
// COMMUNICATION OFFICIELLE PRIsME AUX PRIMES
// ============================================

export const communicationPRIsMEPrimes: CommunicationPRIsME = {
  id: 'comm-prisme-primes-001',
  titre: 'Communication Officielle PRIsME aux Primes',
  date: '2026-01-15',
  heure: '14:00',
  dureeMinutes: 57,
  lieu: 'Salle de réunion principale / Visioconférence',
  objectifPrincipal: 'Présenter PRIsME aux Primes de BZ : vision, architecture, valeur client et rôle des Primes dans l\'excellence opérationnelle. Cette première communication officielle vise à aligner les Primes sur la vision PRIsME et à les positionner comme ambassadeurs de l\'excellence.',
  statut: 'Planifiée',
  participants: participantsComitePRIsME,
  ordreDuJour: [
    {
      id: 'odj-0',
      numero: 1,
      titre: 'L\'Origine et le Pourquoi PRIsME',
      description: 'Comprendre d\'où vient PRIsME : l\'histoire, le déclic, la décision. Comprendre pourquoi PRIsME existe : PRIME + "s" = profondeur systémique, excellence à tous les angles.',
      presentateur: participantsComitePRIsME[1], // Eric Hains
      dureeMinutes: 8,
      niveau: 'Stratégique',
      statut: 'Planifié',
      points: [
        {
          id: 'p0-1',
          titre: 'L\'Origine : Un Appel, Un Problème, Une Décision',
          contenu: `Tout a commencé par deux appels de Jean-Pierre Leclerc à Dany Bourassa en octobre 2025.

• **Premier appel (début octobre)** : Problème de gestion de charge de projet chez BZ Inc. Un enjeu qui persiste, qui ralentit l'efficacité.

• **Deuxième appel (14 octobre)** : Problème de formation et d'éducation d'un Prime. Comment former efficacement? Comment transmettre le savoir? Comment ne pas perdre la connaissance?

**Le déclic** : "Ce problème de formation s'aligne parfaitement avec le problème de charge de projet d'avant. Tout est lié. Tout est systémique." — Dany Bourassa

**La décision** : "C'est rendu mon problème." — Dany Bourassa

Après des années chez BZ Inc., après avoir accumulé tant de savoir, tant d'expérience... Le moment était venu de structurer tout ça. En 10 jours, PRIsME est né.`,
          dureeMinutes: 4,
          graphiques: [],
          concepts: ['Origine', 'Déclic', 'Décision']
        },
        {
          id: 'p0-2',
          titre: 'Pourquoi PRIsME ? PRIME + "S" = Profondeur Systémique',
          contenu: `Le nom PRIsME porte toute notre histoire et notre vision.

**PRIME** : Depuis 25 ans, PRIME est le terme utilisé à l'interne chez BZ Inc. pour désigner le technicien haut de gamme de référence pour un client. C'est notre excellence technique, notre ADN, notre histoire.

**Le "S" de Profondeur** : En ajoutant le "s" à PRIME, nous créons PRIsME. Ce "s" symbolise toute la profondeur de notre approche. Il démontre que nous pensons à tous les angles, à toutes les dimensions, à toutes les facettes de l'excellence TI. Ce n'est plus un seul PRIME, mais une vision systémique qui englobe tous les aspects de l'organisation.

**L'Intelligence Artificielle** : Bien que l'IA ne soit pas dans le nom, elle est au cœur de PRIsME. C'est le catalyseur qui permet de structurer, d'organiser et d'amplifier toute cette profondeur. L'IA amplifie l'humain, ne le remplace pas.

**PRIsME = PRIME + "S" + IA**

Un nom qui porte notre histoire (PRIME), notre vision systémique (le "s"), et la puissance technologique (IA) pour structurer et amplifier l'excellence TI de BZ Inc.`,
          dureeMinutes: 4,
          graphiques: [],
          concepts: ['PRIME', 'Profondeur', 'Systémique', 'IA']
        }
      ]
    },
    {
      id: 'odj-1',
      numero: 2,
      titre: 'PRIsME : Vision Macro et Positionnement',
      description: 'Présentation de la vision stratégique PRIsME, son positionnement marché et sa valeur différenciatrice pour BZ et nos clients.',
      presentateur: participantsComitePRIsME[0], // Jean-Pierre
      dureeMinutes: 10,
      niveau: 'Stratégique',
      statut: 'Planifié',
      points: [
        {
          id: 'p1-1',
          titre: 'PRIsME : Qu\'est-ce que c\'est ?',
          contenu: `PRIsME est notre plateforme d'excellence TI qui transforme notre façon de livrer de la valeur à nos clients. C'est à la fois un outil de gouvernance, un référentiel de connaissances et un générateur de documents professionnels normés.

• Vision : Standardiser l'excellence TI tout en personnalisant chaque relation client
• Positionnement : Différenciateur marché qui positionne BZ comme leader en excellence TI
• Résultats : 10-15 clients déjà livrés avec des retours exceptionnels sur le professionnalisme et la personnalisation

**Architecture PRIsME - Flux Master :**

{{CHART:FLOWCHART:PRISME_ARCHITECTURE:{"steps":["PRIsME Master BZ","Propagation","PRIsME Clients","PRIsME Employés"],"title":"Architecture PRIsME - Flux Master"}}}

**Répartition des Clients Livrés :**

{{CHART:BAR:CLIENTS_LIVRES:{"phases":["Clients Actifs","En Déploiement","En Planification"],"values":[12,3,25],"title":"Clients PRIsME - État Actuel"}}}`,
          dureeMinutes: 4,
          graphiques: ['Flowchart PRIsME Master → Clients & Employés'],
          concepts: ['Vision stratégique', 'Positionnement marché']
        },
        {
          id: 'p1-2',
          titre: 'Pourquoi PRIsME maintenant ?',
          contenu: `Le marché TI évolue : les clients exigent plus de transparence, de personnalisation et de valeur mesurable. PRIsME répond à ces besoins tout en nous permettant de déployer notre excellence à grande échelle (scaler).

• Contexte marché : Clients plus exigeants, besoin de standardisation sans perte de personnalisation
• Opportunité BZ : Transformer notre expertise en avantage concurrentiel durable
• Impact : Amélioration de la satisfaction client, réduction des délais, standardisation de la qualité

**Satisfaction Client - Retours Exceptionnels :**

{{CHART:BAR:SATISFACTION_CLIENT:{"phases":["Professionnalisme","Personnalisation","Qualité Documents","Valeur Perçue"],"values":[95,92,94,96],"title":"Satisfaction Client PRIsME (%)"}}}

**Timeline Déploiement PRIsME :**

{{CHART:LINE:DEPLOIEMENT_PRISME:{"months":["Novembre","Décembre","Janvier","Mars"],"values":[2,15,30,50],"title":"Croissance Clients PRIsME"}}}`,
          dureeMinutes: 3,
          graphiques: ['Bar chart Satisfaction Client', 'Timeline Déploiement PRIsME'],
          concepts: ['Contexte marché', 'Opportunité stratégique']
        },
        {
          id: 'p1-3',
          titre: 'Le Rêve PRIsME : Excellence à Grande Échelle',
          contenu: `PRIsME permet à chaque Prime de livrer de l'excellence constante, personnalisée et mesurable. C'est votre outil pour briller avec vos clients.

• Excellence standardisée : Chaque livrable respecte les normes ITIL/COBIT/NIST
• Personnalisation : Chaque client a son PRIsME unique, alimenté par le PRIsME Master
• Mesurabilité : KPIs clairs, maturité mesurable, valeur démontrable
• Simplicité : L'IA simplifie le complexe, vous vous concentrez sur la valeur client

**Maturité TI - Avant vs Après PRIsME :**

{{CHART:SPIDER:MATURITY:{"axes":["Gouvernance","Processus","Documentation","Personnalisation","Qualité"],"values":[2.5,2.8,2.2,2.0,2.6],"title":"Maturité TI - Avant PRIsME"}}}

{{CHART:SPIDER:MATURITY:{"axes":["Gouvernance","Processus","Documentation","Personnalisation","Qualité"],"values":[4.2,4.5,4.8,4.3,4.6],"title":"Maturité TI - Avec PRIsME"}}}

**Processus PRIsME - De la Demande au Livrable :**

{{CHART:FLOWCHART:PROCESSUS_PRISME:{"steps":["Prise de Besoin","Génération IA","Validation Expert","Personnalisation","Livraison Client"],"title":"Processus PRIsME - Création de Livrable"}}}`,
          dureeMinutes: 3,
          graphiques: ['Spider chart Maturité TI', 'Flowchart Processus PRIsME'],
          concepts: ['Excellence opérationnelle', 'Scalabilité', 'Valeur client']
        }
      ]
    },
    {
      id: 'odj-2',
      numero: 3,
      titre: 'Architecture PRIsME : Master, Clients & Employés',
      description: 'Comprendre l\'architecture PRIsME : le PRIsME Master (source unique de vérité), les PRIsME Clients (personnalisés) et les PRIsME Employés (gouvernance interne).',
      presentateur: participantsComitePRIsME[1], // Eric Hains
      dureeMinutes: 12,
      niveau: 'Tactique',
      statut: 'Planifié',
      points: [
        {
          id: 'p2-1',
          titre: 'PRIsME Master : La Source Unique de Vérité',
          contenu: `Le PRIsME Master BZ est notre référentiel central qui contient toute notre expertise, nos standards, nos cadres et nos meilleures pratiques.

• Contenu : Cadres BZ, standards ITIL/COBIT/NIST, processus, templates, connaissances
• Alimentation : Gestionnaires de départements, experts, amélioration continue
• Propagation : Alimente automatiquement les PRIsME Clients et Employés
• Gouvernance : Chaque département est responsable de son contenu, validation par le CE

**Architecture PRIsME Master :**

{{CHART:FLOWCHART:MASTER_ARCHITECTURE:{"steps":["Gestionnaires Départements","PRIsME Master","Propagation Automatique","PRIsME Clients","PRIsME Employés"],"title":"Architecture PRIsME Master - Source Unique"}}}

**Gouvernance PRIsME - Rôles et Responsabilités :**

{{CHART:MATRIX:GOUVERNANCE_PRISME:{}}}

**Répartition du Contenu Master par Département :**

{{CHART:BAR:CONTENU_MASTER:{"phases":["Service TI","Cybersécurité","Ventes","Admin RH","Infonuagique","Programmation IA"],"values":[35,28,18,12,15,22],"title":"Contenu PRIsME Master par Département (%)"}}}`,
          dureeMinutes: 4,
          graphiques: ['Flowchart PRIsME Master Architecture', 'Diagramme Gouvernance PRIsME'],
          concepts: ['Source unique de vérité', 'Gouvernance', 'Propagation']
        },
        {
          id: 'p2-2',
          titre: 'PRIsME Clients : Personnalisation à Grande Échelle',
          contenu: `Chaque client a son PRIsME unique, alimenté par le Master mais personnalisé selon ses besoins, son contexte et sa maturité TI.

• Personnalisation : Contenu adapté au client, contexte spécifique, maturité mesurée
• Alimentation : Données de départ pré-configurées + interactions client
• Valeur : Le client se sent unique, pas un numéro, relation privilégiée
• Rôle Prime : Vous êtes le gardien du PRIsME de vos clients, vous l'enrichissez et le maintenez

**Processus de Personnalisation PRIsME Client :**

{{CHART:FLOWCHART:PERSONNALISATION_CLIENT:{"steps":["PRIsME Master","Données Client","Personnalisation Prime","PRIsME Client Unique","Enrichissement Continu"],"title":"Personnalisation PRIsME Client"}}}

**Maturité Client - Exemple Type :**

{{CHART:SPIDER:MATURITY:{"axes":["Gouvernance","Infrastructure","Sécurité","Applications","Support"],"values":[3.2,3.5,2.8,3.0,3.3],"title":"Maturité TI Client - Évaluation Initiale"}}}

**Niveau de Personnalisation par Type de Client :**

{{CHART:BAR:PERSONNALISATION_CLIENT:{"phases":["Client Premium","Client Standard","Client Émergent"],"values":[95,75,60],"title":"Niveau de Personnalisation PRIsME Client (%)"}}}`,
          dureeMinutes: 4,
          graphiques: ['Flowchart PRIsME Client Personnalisation', 'Spider chart Maturité Client'],
          concepts: ['Personnalisation', 'Relation client', 'Rôle Prime']
        },
        {
          id: 'p2-3',
          titre: 'PRIsME Employés : Gouvernance Interne',
          contenu: `Chaque employé BZ a également son PRIsME, alimenté par le Master et par TactiCE (gestion stratégique) et l'outil RH. C'est notre façon de personnaliser la relation interne.

• Alimentation : PRIsME Master + TactiCE + Outil RH
• Gouvernance : Gestionnaire responsable du PRIsME de ses employés
• Valeur : Chaque employé a accès à son contenu personnalisé, ses objectifs, ses compétences
• Vision : Même excellence en interne qu'en externe

**Architecture PRIsME Employés :**

{{CHART:FLOWCHART:PRISME_EMPLOYES:{"steps":["PRIsME Master","TactiCE","Outil RH","PRIsME Employé","Gestionnaire"],"title":"Architecture PRIsME Employés"}}}

**Gouvernance PRIsME Employés - RACI :**

{{CHART:MATRIX:GOUVERNANCE_EMPLOYES:{}}}

**Adoption PRIsME Employés par Département :**

{{CHART:BAR:ADOPTION_EMPLOYES:{"phases":["Service TI","Cybersécurité","Ventes","Admin RH"],"values":[85,78,65,70],"title":"Taux d'Adoption PRIsME Employés (%)"}}}`,
          dureeMinutes: 4,
          graphiques: ['Flowchart PRIsME Employés', 'Diagramme Gouvernance Employés'],
          concepts: ['Gouvernance interne', 'Personnalisation employé', 'Excellence interne']
        }
      ]
    },
    {
      id: 'odj-3',
      numero: 4,
      titre: 'Le Concept de l\'Arbre : Tronc, Branches, Feuilles',
      description: 'Comprendre la philosophie PRIsME : l\'arbre représente notre structure de connaissances (Tronc = fondamentaux, Branches = départements, Feuilles = livrables clients).',
      presentateur: participantsComitePRIsME[2], // Francis Marquis
      dureeMinutes: 10,
      niveau: 'Stratégique',
      statut: 'Planifié',
      points: [
        {
          id: 'p3-1',
          titre: 'Le Tronc : Fondamentaux BZ',
          contenu: `Le tronc représente les fondamentaux BZ : nos cadres référentiels, nos standards, nos concepts de base. C'est ce qui ne change jamais et qui fait notre ADN.

• Contenu : Cadre BZ, Cadre NIST, concepts fondamentaux (ITIL, COBIT, etc.)
• Stabilité : Le tronc évolue lentement, c'est notre fondation
• Propagation : Le tronc alimente toutes les branches
• Rôle : Chaque Prime doit comprendre le tronc pour exceller

**Concept de l'Arbre PRIsME - Vue d'Ensemble :**

{{CHART:FLOWCHART:ARBRE_PRISME:{"steps":["Tronc (Fondamentaux)","Branches (Départements)","Feuilles (Livrables)"],"title":"Concept de l'Arbre PRIsME"}}}

**Maturité Cadre BZ - 7 Piliers :**

{{CHART:SPIDER:MATURITY:{"axes":["Besoins & Alignement","Catalogue Actifs","Projets & Budget","Opérations TI","Cybersécurité","Innovation","IA & Programmation"],"values":[4.2,3.8,4.0,4.3,4.1,3.9,4.2],"title":"Maturité Cadre BZ - 7 Piliers Stratégiques"}}}

**Répartition Contenu Tronc :**

{{CHART:BAR:CONTENU_TRONC:{"phases":["Cadre BZ","Standards ITIL","Standards COBIT","Standards NIST","Concepts Base"],"values":[30,25,20,15,10],"title":"Répartition Contenu Tronc PRIsME (%)"}}}`,
          dureeMinutes: 3,
          graphiques: ['Diagramme Arbre PRIsME', 'Spider chart Cadre BZ'],
          concepts: ['Fondamentaux', 'Stabilité', 'ADN BZ']
        },
        {
          id: 'p3-2',
          titre: 'Les Branches : Départements et Domaines',
          contenu: `Les branches représentent nos départements : Service & Direction TI, Cybersécurité, Ventes & Marketing, etc. Chaque branche a sa spécialité mais s'alimente du tronc.

• Structure : Chaque département = une branche avec son contenu spécialisé
• Alimentation : Branche s'alimente du tronc + expertise département
• Évolution : Les branches évoluent plus rapidement que le tronc
• Rôle Prime : Vous travaillez dans une ou plusieurs branches, vous les enrichissez

**Propagation Tronc → Branches :**

{{CHART:FLOWCHART:PROPAGATION_BRANCHES:{"steps":["Tronc (Fondamentaux)","Service TI","Cybersécurité","Ventes","Admin RH","Infonuagique","Programmation IA"],"title":"Propagation Tronc → Branches PRIsME"}}}

**Contenu par Branche (Département) :**

{{CHART:BAR:CONTENU_BRANCHES:{"phases":["Service TI","Cybersécurité","Ventes","Admin RH","Infonuagique","Programmation IA"],"values":[45,38,33,22,28,35],"title":"Nombre de Documents par Branche"}}}

**Évolution Contenu Branches (6 derniers mois) :**

{{CHART:LINE:EVOLUTION_BRANCHES:{"months":["Mois 1","Mois 2","Mois 3","Mois 4","Mois 5","Mois 6"],"values":[120,135,150,165,180,201],"title":"Croissance Contenu Branches PRIsME"}}}`,
          dureeMinutes: 3,
          graphiques: ['Diagramme Branches PRIsME', 'Flowchart Propagation Branches'],
          concepts: ['Départements', 'Spécialisation', 'Évolution']
        },
        {
          id: 'p3-3',
          titre: 'Les Feuilles : Livrables Clients',
          contenu: `Les feuilles représentent les livrables que nous créons pour nos clients : documents, analyses, recommandations. Chaque feuille est unique mais s'alimente des branches et du tronc.

• Personnalisation : Chaque feuille (livrable) est unique au client
• Alimentation : Feuille s'alimente du tronc + branches pertinentes
• Qualité : Chaque feuille respecte nos standards d'excellence
• Rôle Prime : Vous créez les feuilles pour vos clients, vous êtes l'artisan de l'excellence

**Processus Création Feuille (Livrable) :**

{{CHART:FLOWCHART:CREATION_LIVRABLE:{"steps":["Tronc + Branches","Formulaire Client","Génération IA","Validation Prime","Livrable Personnalisé"],"title":"Création Feuille PRIsME - Processus Complet"}}}

**Qualité Livrables par Niveau :**

{{CHART:BAR:QUALITE_LIVRABLES:{"phases":["Niveau 1","Niveau 2","Niveau 3","Niveau 4","Niveau 5"],"values":[15,25,35,18,7],"title":"Distribution Livrables par Niveau de Qualité (%)"}}}`,
          dureeMinutes: 4,
          graphiques: ['Diagramme Feuilles PRIsME', 'Flowchart Création Livrable'],
          concepts: ['Livrables', 'Personnalisation', 'Excellence']
        }
      ]
    },
    {
      id: 'odj-4',
      numero: 5,
      titre: 'Gestion Documentaire : La Richesse de Contenu',
      description: 'La gestion documentaire PRIsME est notre atout majeur : génération de documents professionnels normés, personnalisés et de qualité exceptionnelle grâce à l\'IA et l\'expertise humaine.',
      presentateur: participantsComitePRIsME[3], // Jean-Michel Caouette
      dureeMinutes: 10,
      niveau: 'Opérationnel',
      statut: 'Planifié',
      points: [
        {
          id: 'p4-1',
          titre: 'Gabarits Documents & Formulaires',
          contenu: `PRIsME contient des gabarits de documents normés (ITIL, COBIT, NIST, etc.) et des formulaires holistiques de prise de besoin. C'est votre bibliothèque d'excellence.

• Gabarits Documents : Plan Directeur, Politiques, Analyses, etc. (5 niveaux de qualité)
• Gabarits Formulaires : Prise de besoin structurée, holistique, adaptée au contexte client
• Normes : 100% normé ITIL/COBIT/NIST/PMBOK/Autre
• Qualité : Chaque gabarit est validé et testé, prêt à l'emploi

**Distribution Documents par Domaine :**

{{CHART:BAR:DISTRIBUTION_DOCUMENTS:{"phases":["TI","Cybersécurité","Ventes","Administration","Employé","Projet"],"values":[45,38,33,22,15,24],"title":"Distribution Documents PRIsME par Domaine"}}}

**Qualité Documents - 5 Niveaux :**

{{CHART:SPIDER:MATURITY:{"axes":["Niveau 1","Niveau 2","Niveau 3","Niveau 4","Niveau 5"],"values":[15,25,35,18,7],"title":"Distribution Qualité Documents PRIsME (%)"}}}

**Normes Couvertes :**

{{CHART:BAR:NORMES_COUVERTES:{"phases":["ITIL","COBIT","NIST","PMBOK","ISO 27001","Autres"],"values":[85,75,70,45,60,30],"title":"Couverture Normes par Type de Document (%)"}}}`,
          dureeMinutes: 3,
          graphiques: ['Bar chart Distribution Documents par Domaine', 'Spider chart Qualité Documents'],
          concepts: ['Gabarits', 'Normes', 'Qualité']
        },
        {
          id: 'p4-2',
          titre: 'Génération IA + Expertise Humaine',
          contenu: `PRIsME génère des documents professionnels en combinant l'IA (GPT-5.2) et l'expertise humaine. L'IA fait le travail lourd, vous ajoutez la valeur stratégique.

• Processus : Formulaire → IA génère document → Expert humain valide et enrichit
• Qualité : 5 niveaux de qualité (1 = brouillon, 5 = niveau CA/DG)
• Graphiques : PRIsME génère automatiquement des graphiques professionnels (spider, bar, flowcharts, matrices)
• Personnalisation : Chaque document utilise les données du formulaire client

**Processus Génération Document PRIsME :**

{{CHART:FLOWCHART:GENERATION_DOCUMENT:{"steps":["Formulaire Client","Génération IA (GPT-5.2)","Document Brut","Validation Expert","Enrichissement","Document Final"],"title":"Processus Génération Document PRIsME"}}}

**Qualité Documents par Niveau :**

{{CHART:BAR:QUALITE_NIVEAU:{"phases":["Niveau 1","Niveau 2","Niveau 3","Niveau 4","Niveau 5"],"values":[15,25,35,18,7],"title":"Distribution Documents par Niveau de Qualité (%)"}}}

**Temps de Génération - IA vs Humain :**

{{CHART:BAR:TEMPS_GENERATION:{"phases":["Génération IA","Validation Expert","Enrichissement","Total"],"values":[5,15,20,40],"title":"Temps de Génération Document (minutes)"}}}`,
          dureeMinutes: 4,
          graphiques: ['Flowchart Génération Document', 'Bar chart Qualité par Niveau'],
          concepts: ['IA + Humain', 'Qualité', 'Personnalisation']
        },
        {
          id: 'p4-3',
          titre: 'Valeur pour le Prime : Simplicité & Excellence',
          contenu: `En tant que Prime, PRIsME vous simplifie la vie tout en garantissant l'excellence. Vous vous concentrez sur la relation client, PRIsME s'occupe du reste.

• Simplicité : Remplir un formulaire, PRIsME génère le document
• Excellence : Chaque document est professionnel, normé, personnalisé
• Temps : Réduction drastique du temps de création de documents
• Confiance : Vos clients voient la qualité, vous brillez

**Gain de Temps - Avant vs Après PRIsME :**

{{CHART:BAR:GAIN_TEMPS:{"phases":["Création Manuelle","Avec PRIsME","Gain de Temps"],"values":[240,40,200],"title":"Temps de Création Document (minutes)"}}}

**Satisfaction Prime - Utilisation PRIsME :**

{{CHART:SPIDER:MATURITY:{"axes":["Simplicité","Qualité","Gain Temps","Satisfaction Client","Valeur Perçue"],"values":[4.5,4.7,4.8,4.6,4.7],"title":"Satisfaction Prime - Utilisation PRIsME"}}}

**Évolution Adoption PRIsME par Primes :**

{{CHART:LINE:ADOPTION_PRIMES:{"months":["Mois 1","Mois 2","Mois 3","Mois 4","Mois 5","Mois 6"],"values":[20,35,50,65,78,85],"title":"Taux d'Adoption PRIsME par Primes (%)"}}}`,
          dureeMinutes: 3,
          graphiques: ['Bar chart Gain de Temps', 'Spider chart Satisfaction Prime'],
          concepts: ['Simplicité', 'Excellence', 'Valeur Prime']
        }
      ]
    },
    {
      id: 'odj-5',
      numero: 6,
      titre: 'Agent IA PRIsME : Simplifier le Complexe',
      description: 'L’agent IA PRIsME est votre assistant intelligent qui répond à vos questions, analyse vos documents et vous guide dans l\'utilisation de PRIsME.',
      presentateur: participantsComitePRIsME[1], // Eric Hains
      dureeMinutes: 8,
      niveau: 'Opérationnel',
      statut: 'Planifié',
      points: [
        {
          id: 'p5-1',
          titre: 'Assistant Intelligent PRIsME',
          contenu: `L’agent IA PRIsME connaît tout PRIsME : cadres, standards, processus, documents. Il répond à vos questions en temps réel et vous guide.

• Connaissance : L’agent a accès à toute la base de connaissances PRIsME
• Réponses : Réponses instantanées, précises, contextuelles
• Guidance : L’agent vous guide dans l'utilisation de PRIsME
• Apprentissage : L’agent s'améliore avec chaque interaction

**Architecture Agent IA PRIsME :**

{{CHART:FLOWCHART:BOT_AI:{"steps":["Question Prime","Base Connaissances PRIsME","GPT-5.2","Réponse Contextuelle","Apprentissage"],"title":"Architecture Agent IA PRIsME"}}}

**Utilisation Agent IA par Type de Question :**

{{CHART:BAR:UTILISATION_BOT:{"phases":["Questions Cadres","Questions Processus","Questions Documents","Questions Standards","Autres"],"values":[35,28,22,10,5],"title":"Utilisation Agent IA par Type (%)"}}}

**Satisfaction attendue (Agent IA) :**

{{CHART:SPIDER:MATURITY:{"axes":["Précision","Rapidité","Utilité","Facilité","Satisfaction"],"values":[4.3,4.7,4.5,4.6,4.5],"title":"Satisfaction attendue (Agent IA PRIsME)"}}}`,
          dureeMinutes: 3,
          graphiques: ['Flowchart Bot AI PRIsME', 'Bar chart Utilisation Bot'],
          concepts: ['IA', 'Assistant', 'Guidance']
        },
        {
          id: 'p5-2',
          titre: 'Analyse de Documents & Recommandations',
          contenu: `L’agent IA peut analyser vos documents, identifier les écarts, suggérer des améliorations et vous recommander les meilleures pratiques.

• Analyse : L’agent analyse vos documents et identifie les points d'amélioration
• Recommandations : Suggestions basées sur les normes ITIL/COBIT/NIST
• Amélioration continue : L’agent vous aide à améliorer vos livrables
• Excellence : Chaque recommandation vise l'excellence

**Processus Analyse Document - Agent IA :**

{{CHART:FLOWCHART:ANALYSE_DOCUMENT:{"steps":["Document Prime","Analyse Agent IA","Identification des écarts","Recommandations","Amélioration"],"title":"Processus Analyse Document - Agent IA"}}}

**Types de Recommandations Agent IA :**

{{CHART:BAR:RECOMMANDATIONS_BOT:{"phases":["Conformité Normes","Structure Document","Qualité Contenu","Graphiques","Complétude"],"values":[32,25,20,15,8],"title":"Types de Recommandations Agent IA (%)"}}}

**Impact Recommandations - Amélioration Qualité :**

{{CHART:LINE:IMPACT_RECOMMANDATIONS:{"months":["Avant","Après 1 mois","Après 3 mois","Après 6 mois"],"values":[2.5,3.5,4.2,4.8],"title":"Amélioration Qualité Documents (sur 5, progression 2.5 → 4.8)"}}}`,
          dureeMinutes: 3,
          graphiques: ['Flowchart Analyse Document', 'Bar chart Recommandations'],
          concepts: ['Analyse', 'Recommandations', 'Amélioration continue']
        },
        {
          id: 'p5-3',
          titre: 'Valeur pour le Prime : Accélération & Qualité',
          contenu: `Le bot AI PRIsME vous fait gagner du temps et améliore la qualité de vos livrables. C'est votre co-pilote vers l'excellence.

• Temps : Réponses instantanées, pas besoin de chercher
• Qualité : Recommandations basées sur les meilleures pratiques
• Confiance : Vous avez toujours une ressource pour vous guider
• Excellence : Le bot vous aide à livrer de l'excellence constante

**Gain de Temps - Utilisation Bot AI :**

{{CHART:BAR:GAIN_TEMPS_BOT:{"phases":["Recherche Manuelle","Avec Bot AI","Gain de Temps"],"values":[30,2,28],"title":"Temps de Recherche Information (minutes)"}}}

**Satisfaction Prime - Bot AI :**

{{CHART:SPIDER:MATURITY:{"axes":["Rapidité","Précision","Utilité","Facilité","Satisfaction"],"values":[4.7,4.3,4.5,4.6,4.5],"title":"Satisfaction Prime - Bot AI PRIsME"}}}

**Évolution Utilisation Bot AI :**

{{CHART:LINE:EVOLUTION_BOT:{"months":["Mois 1","Mois 2","Mois 3","Mois 4","Mois 5","Mois 6"],"values":[50,120,200,320,450,580],"title":"Nombre de Questions Bot AI par Mois"}}}`,
          dureeMinutes: 2,
          graphiques: ['Bar chart Gain de Temps Bot', 'Spider chart Satisfaction Bot'],
          concepts: ['Accélération', 'Qualité', 'Co-pilote']
        }
      ]
    },
    {
      id: 'odj-6',
      numero: 7,
      titre: 'Rôle des Primes : Ambassadeurs de l\'Excellence',
      description: 'Les Primes sont au cœur de PRIsME : vous êtes les ambassadeurs de l\'excellence, les gardiens de la qualité et les artisans de la relation client personnalisée.',
      presentateur: participantsComitePRIsME[0], // Jean-Pierre
      dureeMinutes: 5,
      niveau: 'Stratégique',
      statut: 'Planifié',
      points: [
        {
          id: 'p6-1',
          titre: 'Votre Rôle : Excellence Opérationnelle',
          contenu: `En tant que Prime, vous êtes responsable de l'excellence opérationnelle avec vos clients. PRIsME est votre outil pour y arriver.

• Responsabilité : Vous êtes responsable de la qualité des livrables pour vos clients
• Outil : PRIsME vous donne les outils pour livrer de l'excellence
• Personnalisation : Vous personnalisez chaque livrable selon le contexte client
• Mesure : Vous mesurez la maturité et l'amélioration continue

**Rôle Prime - Processus Complet :**

{{CHART:FLOWCHART:ROLE_PRIME:{"steps":["Relation Client","Prise de Besoin","Génération PRIsME","Personnalisation","Validation","Livraison"],"title":"Rôle Prime - Processus Excellence"}}}

**Responsabilités Prime - Matrice :**

{{CHART:MATRIX:RESPONSABILITES_PRIME:{}}}

**Maturité Prime - Compétences PRIsME :**

{{CHART:SPIDER:MATURITY:{"axes":["Maîtrise PRIsME","Personnalisation","Qualité Livrables","Relation Client","Amélioration Continue"],"values":[4.2,4.5,4.6,4.7,4.3],"title":"Maturité Prime - Compétences PRIsME"}}}`,
          dureeMinutes: 2,
          graphiques: ['Flowchart Rôle Prime', 'Spider chart Responsabilités Prime'],
          concepts: ['Responsabilité', 'Excellence', 'Personnalisation']
        },
        {
          id: 'p6-2',
          titre: 'Votre Impact : Satisfaction Client & Croissance',
          contenu: `Votre utilisation de PRIsME a un impact direct sur la satisfaction client et la croissance de BZ. Vous êtes les artisans du succès.

• Satisfaction : Clients impressionnés par le professionnalisme et la personnalisation
• Croissance : PRIsME différencie BZ, génère de nouvelles opportunités
• Excellence : Chaque Prime qui utilise PRIsME élève le niveau de BZ
• Rêve : PRIsME vous permet de réaliser le rêve d'excellence à grande échelle

**Satisfaction Client - Impact PRIsME :**

{{CHART:BAR:SATISFACTION_IMPACT:{"phases":["Avant PRIsME","Avec PRIsME","Amélioration"],"values":[72,94,22],"title":"Satisfaction Client (%)"}}}

**Impact Prime - Valeur Créée :**

{{CHART:SPIDER:MATURITY:{"axes":["Satisfaction Client","Qualité Livrables","Efficacité","Innovation","Croissance"],"values":[4.6,4.7,4.5,4.4,4.6],"title":"Impact Prime - Valeur Créée avec PRIsME"}}}`,
          dureeMinutes: 3,
          graphiques: ['Bar chart Satisfaction Client', 'Line chart Croissance BZ'],
          concepts: ['Impact', 'Satisfaction', 'Croissance']
        }
      ]
    },
    {
      id: 'odj-7',
      numero: 8,
      titre: 'Mot de la fin : Devinette',
      description: 'Clôture ludique : devine en combien de temps cette communication a été bâtie. Révélation après un suspense contrôlé.',
      presentateur: participantsComitePRIsME[1], // Eric Hains
      dureeMinutes: 4,
      niveau: 'Tactique',
      statut: 'Planifié',
      points: [
        {
          id: 'p7-1',
          titre: 'Devine le temps de création',
          contenu: '__DEVINETTE__',
          dureeMinutes: 4,
          graphiques: [],
          concepts: ['Jeu', 'Suspense', 'Cadeau']
        }
      ]
    }
  ]
};

